import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";

import {
    formatNumberWithCommas,
    dateFormatDateTimeApi,
    generateHourOptions,
    generateMinuteOptions,
    combineDateTime,
    extractDateTime
} from "@/utils";
import { fuelRateOptions, washStatusOptions } from "@/constants/selectOptions";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

export const ReturnInfo = ({ setFormData, allocation, onAllocationChange }) => {
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressTarget, setAddressTarget] = useState(""); // "release" or "return"

    const releaseMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];
    const returnMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];

    // 공통 함수에서 옵션 생성
    const hourOptions = generateHourOptions();
    const minuteOptions = generateMinuteOptions();

    // 주소 검색 모달에서 선택된 주소 데이터를 처리하는 콜백
    const handleAddressSelect = (data) => {
        if (addressTarget === "release") {
            onAllocationChange({ releasebCode: data.bcode, releaseAddress: data.address });
        } else if (addressTarget === "return") {
            onAllocationChange({ returnbCode: data.bcode, returnAddress: data.address });
        }
        setIsAddressModalOpen(false);
        setAddressTarget("");
    };

    const handleAllocationChange = (newValues) => {
        const newAllocation = { ...allocation, ...newValues };
        // 두 날짜 모두 설정되어 있으면 날짜 비교 (같은 날짜도 허용하지 않음)
        if (newAllocation.releasedAt && newAllocation.returnedAt) {
            const released = new Date(newAllocation.releasedAt);
            const returned = new Date(newAllocation.returnedAt);
            if (returned <= released) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "반납일시가 배차일시보다 빠르거나 같을 수 없습니다.",
                }));
                return;
            }
        }
        onAllocationChange(newValues);
    };

    // 배차일시 관련 핸들러
    const handleReleaseDateChange = (date) => {
        const { hour, minute } = extractDateTime(allocation?.releasedAt);
        const combinedDateTime = combineDateTime(date, hour, minute);
        handleAllocationChange({ releasedAt: combinedDateTime ? dateFormatDateTimeApi(combinedDateTime) : null });
    };

    const handleReleaseTimeChange = (field, value) => {
        const { date, hour, minute } = extractDateTime(allocation?.releasedAt);
        const newHour = field === 'hour' ? value : hour;
        const newMinute = field === 'minute' ? value : minute;
        const combinedDateTime = combineDateTime(date, newHour, newMinute);
        handleAllocationChange({ releasedAt: combinedDateTime ? dateFormatDateTimeApi(combinedDateTime) : null });
    };

    // 반납일시 관련 핸들러
    const handleReturnDateChange = (date) => {
        const { hour, minute } = extractDateTime(allocation?.returnedAt);
        const combinedDateTime = combineDateTime(date, hour, minute);
        handleAllocationChange({ returnedAt: combinedDateTime ? dateFormatDateTimeApi(combinedDateTime) : null });
    };

    const handleReturnTimeChange = (field, value) => {
        const { date, hour, minute } = extractDateTime(allocation?.returnedAt);
        const newHour = field === 'hour' ? value : hour;
        const newMinute = field === 'minute' ? value : minute;
        const combinedDateTime = combineDateTime(date, newHour, newMinute);
        handleAllocationChange({ returnedAt: combinedDateTime ? dateFormatDateTimeApi(combinedDateTime) : null });
    };

    const releaseDateTime = extractDateTime(allocation?.releasedAt);
    const returnDateTime = extractDateTime(allocation?.returnedAt);

    return (
        <>
            <div className="item-row" data-grow="2/3">
                <div className="row-head">
                    <span>배차일시</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={releaseDateTime.date}
                            onChange={handleReleaseDateChange}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                            placeholderText="날짜 선택"
                        />
                    </div>
                    <div className="form-element" style={{ flexShrink: 0, width: "100px" }}>
                        <Select
                            options={hourOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="시간"
                            value={hourOptions.find(opt => opt.value === releaseDateTime.hour)}
                            onChange={(option) => handleReleaseTimeChange('hour', option.value)}
                        />
                    </div>
                    <div className="form-element" style={{ flexShrink: 0, width: "100px" }}>
                        <Select
                            options={minuteOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="분"
                            value={minuteOptions.find(opt => opt.value === releaseDateTime.minute)}
                            onChange={(option) => handleReleaseTimeChange('minute', option.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배차방법</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={releaseMethodOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="배차방법"
                            value={releaseMethodOptions.find((opt) => opt.value === allocation?.releaseMethod)}
                            onChange={(option) => onAllocationChange({ releaseMethod: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>배차주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="releasebCode"
                            value={allocation?.releasebCode || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("release");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="releaseAddress"
                            value={allocation?.releaseAddress || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("release");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="releaseAddressDetail"
                            value={allocation?.releaseAddressDetail || ""}
                            onChange={(e) => onAllocationChange({ releaseAddressDetail: e.target.value })}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() => {
                                setAddressTarget("release");
                                setIsAddressModalOpen(true);
                            }}
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="2/3">
                <div className="row-head">
                    <span>반납일시</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={returnDateTime.date}
                            onChange={handleReturnDateChange}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                            placeholderText="날짜 선택"
                        />
                    </div>
                    <div className="form-element" style={{ flexShrink: 0, width: "100px" }}>
                        <Select
                            options={hourOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="시간"
                            value={hourOptions.find(opt => opt.value === returnDateTime.hour)}
                            onChange={(option) => handleReturnTimeChange('hour', option.value)}
                        />
                    </div>
                    <div className="form-element" style={{ flexShrink: 0, width: "100px" }}>
                        <Select
                            options={minuteOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="분"
                            value={minuteOptions.find(opt => opt.value === returnDateTime.minute)}
                            onChange={(option) => handleReturnTimeChange('minute', option.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>반납방법</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={returnMethodOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="반납방법"
                            value={returnMethodOptions.find((opt) => opt.value === allocation?.returnMethod)}
                            onChange={(option) => onAllocationChange({ returnMethod: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>반납 주행거리</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="mileage"
                            placeholder=""
                            value={allocation?.returnMileage || ""}
                            onChange={(e) => onAllocationChange({ returnMileage: e.target.value })}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">km</span>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>반납 유류량</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유류량 선택"
                            value={fuelRateOptions.find((opt) => opt.value === allocation?.returnGasAmount)}
                            onChange={(option) => onAllocationChange({ returnGasAmount: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>세차상태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={washStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="세차상태 선택"
                            value={washStatusOptions.find((opt) => opt.value === allocation?.washStatus)}
                            onChange={(option) => onAllocationChange({ washStatus: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>반납주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="returnbCode"
                            value={allocation?.returnbCode || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("return");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="returnAddress"
                            value={allocation?.returnAddress || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("return");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="returnAddressDetail"
                            value={allocation?.returnAddressDetail || ""}
                            onChange={(e) => onAllocationChange({ returnAddressDetail: e.target.value })}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() => {
                                setAddressTarget("return");
                                setIsAddressModalOpen(true);
                            }}
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea
                            name="memo"
                            placeholder=""
                            value={allocation?.memo || ""}
                            onChange={(e) => onAllocationChange({ memo: e.target.value })}
                        />
                    </div>
                </div>
            </div>
            {isAddressModalOpen && (
                <SearchAddress modalStatus={isAddressModalOpen} setModalStatus={setIsAddressModalOpen} setAddress={handleAddressSelect} />
            )}
        </>
    );
};
